import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography,
    Input
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faTimes, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../axiosInstance';
import { rootUrl } from '../../../config/config';
import { formatMoney } from './formatMoney';

const ReverseTransactionDialog = ({ open, onClose, ownerCode, transaction, onSuccess, onReverseSuccess }) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    // Extract ownerCode from transaction if not provided as prop
    const code = ownerCode || transaction?.owner_code;

    useEffect(() => {
        if (open) {
            setReason('');
            setError('');
            setSubmitting(false);
        }
    }, [open]);

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!reason.trim()) {
            setError('Reason is required to reverse a transaction');
            return;
        }
        if (!code) {
            setError('Owner code is missing');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const url = `${rootUrl}/api/owners/${code}/transactions/${transaction.id}/reverse`;
            const res = await axiosInstance.post(url, { reason });
            if (res.data && res.data.success) {
                const reversalData = res.data.data;
                // Call both callbacks for backward compatibility
                onSuccess && onSuccess(reversalData);
                onReverseSuccess && onReverseSuccess({
                    reversalTransId: reversalData?.id,
                    originalTransId: transaction.id,
                    reason,
                    reversedBy: reversalData?.input_by
                });
                onClose && onClose();
            } else {
                setError(res.data?.message || 'Failed to reverse transaction');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error reversing transaction');
        } finally {
            setSubmitting(false);
        }
    };

    if (!transaction) return null;

    return (
        <Dialog open={open} handler={onClose} size="md">
            <DialogHeader>
                <FontAwesomeIcon icon={faRotateLeft} className="mr-2" />
                Reverse Transaction
            </DialogHeader>
            <form onSubmit={handleSubmit}>
                <DialogBody>
                    <div className="mb-4 p-3 rounded bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm flex items-start">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 mr-2" />
                        <div>
                            This will create a reversal entry that offsets the original.
                        </div>
                    </div>

                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-600">ID</span>
                            <span className="font-mono">{transaction.id}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Date</span>
                            <span>{new Date(transaction.date).toLocaleDateString('en-KE')}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Type</span>
                            <span>{transaction.trans_type}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Amount</span>
                            <span>{formatMoney(transaction.amount)}</span>
                        </div>
                        {transaction.description && (
                            <div>
                                <div className="text-gray-600">Description</div>
                                <div className="text-gray-800">{transaction.description}</div>
                            </div>
                        )}
                    </div>

                    <div className="mt-4">
                        <Input
                            label="Reason for reversal *"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            error={!!error && !reason.trim()}
                        />
                        {error && (
                            <Typography variant="small" color="red" className="mt-2">
                                {error}
                            </Typography>
                        )}
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button variant="text" onClick={onClose} className="mr-2">
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Cancel
                    </Button>
                    <Button color="red" type="submit" disabled={submitting}>
                        <FontAwesomeIcon icon={faRotateLeft} className="mr-2" />
                        {submitting ? 'Reversing...' : 'Reverse'}
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
};

export default ReverseTransactionDialog;
